import { Store } from '@app/types/store';
import { Card, CardContent, Pulse } from 'easyship-components';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';
import CSVSVG from '../svg/csv.svg';
import ManualInputSVG from '../svg/manual-input.svg';
import PlatformImage from './PlatformImage';
import VariousLogos from './VariousLogos';

const EMPTY_ADVANCED_PREFIX = 'shipments.list.empty.advanced';

export enum ActionCardType {
  SyncOrders = 'sync-orders',
  ManualInput = 'manual-input',
  ImportFile = 'import-file',
}

const BG_COLOR = {
  [ActionCardType.SyncOrders]: 'bg-[#d2f1f2]',
  [ActionCardType.ManualInput]: 'bg-yellow-100',
  [ActionCardType.ImportFile]: 'bg-green-100',
} as const;

export default function ActionCard({
  type,
  storeCount,
  store,
  onClick,
  isWorking,
}: {
  type: ActionCardType;
  storeCount?: number;
  store?: Store;
  onClick: () => void;
  isWorking?: boolean;
}) {
  const syncOrdersDescPostfix = useMemo(() => {
    if (type !== ActionCardType.SyncOrders || storeCount === undefined) {
      return '';
    }

    switch (storeCount) {
      case 0:
        return '-no-stores';
      case 1:
        return '-one-store';
      default:
        return '-multiple-stores';
    }
  }, [type, storeCount]);

  const shouldShowPlatformImage = storeCount === 1 && store?.platform;

  return (
    <Card
      onClick={!isWorking ? onClick : undefined}
      className="flex-1 h-full rounded-xl border-none bg-white relative shadow-md transform hover:-translate-y-1 transition-all"
    >
      <Pulse show={isWorking} />
      <CardContent className="p-0">
        <div
          className={twMerge(
            'w-full h-[216px] flex justify-center items-center rounded-t-xl',
            BG_COLOR[type]
          )}
        >
          {type === ActionCardType.SyncOrders &&
            (shouldShowPlatformImage ? <PlatformImage store={store} /> : <VariousLogos />)}
          {type === ActionCardType.ManualInput && (
            <img src={ManualInputSVG} alt="manual input" width="192" height="192" />
          )}
          {type === ActionCardType.ImportFile && (
            <img src={CSVSVG} alt="CSV" width="192" height="192" />
          )}
        </div>
        <div className="flex justify-center items-center w-full h-[122px]">
          <div className="flex flex-col gap-2 items-center w-full">
            <h3 className="text-xl text-ink-900">
              <FormattedMessage id={`${EMPTY_ADVANCED_PREFIX}.${type}`} />
            </h3>
            <div
              className={twMerge(
                'text-base text-ink-500 text-center',
                syncOrdersDescPostfix === '-no-stores' && 'max-w-[70%]'
              )}
            >
              <FormattedMessage
                id={`${EMPTY_ADVANCED_PREFIX}.${type}-desc${syncOrdersDescPostfix}`}
                values={{
                  store: store?.name ?? '',
                }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
